import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { FaRobot } from "react-icons/fa";
import { HiChatBubbleLeftRight } from "react-icons/hi2";
import { IoClose, IoSend, IoSparkles } from "react-icons/io5";

const AIChatWidget = ({ isOpen: externalIsOpen, onToggle }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "ai",
      text: "Hello! I'm Zero-AI, your intelligent blockchain assistant. How can I help you today?",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const orbRef = useRef(null);

  // Sync with external control
  useEffect(() => {
    if (externalIsOpen !== undefined) {
      setIsOpen(externalIsOpen);
    }
  }, [externalIsOpen]);

  // Listen for openAIChat custom event
  useEffect(() => {
    const handleOpenChat = () => setIsOpen(true);
    window.addEventListener('openAIChat', handleOpenChat);
    return () => window.removeEventListener('openAIChat', handleOpenChat);
  }, []);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Floating orb animation - using CSS instead of GSAP for stability
  // Removed GSAP animation that may cause issues with component lifecycle


  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    if (onToggle) onToggle(newState);
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      type: "user",
      text: inputValue,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");

    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const aiResponse = {
        id: messages.length + 2,
        type: "ai",
        text: getAIResponse(inputValue),
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, aiResponse]);
    }, 1500 + Math.random() * 1000);
  };

  const getAIResponse = (userInput) => {
    const lower = userInput.toLowerCase();
    if (lower.includes("wallet") || lower.includes("crypto")) {
      return "Zero-Crypto-Wallet is our flagship digital wallet solution with multi-chain support, advanced security features, and DeFi integration. Would you like to learn more about its features?";
    } else if (lower.includes("ico") || lower.includes("token")) {
      return "Our ICO is currently in progress! Zero-Coin is available at $0.08 per token. You can participate through our secure ICO page with multiple payment options.";
    } else if (lower.includes("support") || lower.includes("help")) {
      return "I'm here to help! For complex issues, you can open a support ticket and our specialists will respond within 15 minutes.";
    } else if (lower.includes("blockchain")) {
      return "Zero-Blockchain is our enterprise-grade infrastructure providing high throughput, security, and scalability for mission-critical applications.";
    } else {
      return "I can assist you with our products, ICO participation, wallet solutions, and blockchain services. What would you like to explore?";
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* SPECTACULAR FLOATING CHAT BUTTON */}
      <AnimatePresence mode="wait">
        {!isOpen && (
          <motion.div
            key="chat-button"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", damping: 12, stiffness: 150 }}
            className="fixed bottom-8 right-8 z-[9998] animate-float"
            style={{ animation: 'float 3s ease-in-out infinite' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {/* Outer animated rings */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.div
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                className="absolute w-20 h-20 rounded-full border-2 border-cyan-400"
              />
              <motion.div
                animate={{ scale: [1, 1.8, 1], opacity: [0.3, 0, 0.3] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.5 }}
                className="absolute w-20 h-20 rounded-full border border-cyan-500"
              />
              <motion.div
                animate={{ scale: [1, 2.2, 1], opacity: [0.2, 0, 0.2] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 1 }}
                className="absolute w-20 h-20 rounded-full border border-teal-400"
              />
            </div>

            {/* Rotating gradient ring */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-2"
            >
              <div className="w-full h-full rounded-full bg-gradient-conic from-cyan-500 via-transparent via-50% to-cyan-500 opacity-60" 
                   style={{ background: 'conic-gradient(from 0deg, #06b6d4, transparent 40%, transparent 60%, #06b6d4)' }} />
            </motion.div>

            {/* Main button */}
            <motion.button
              onClick={handleToggle}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="relative w-20 h-20 rounded-full cursor-pointer focus:outline-none"
              aria-label="Open AI Chat"
            >
              {/* Gradient background with glow */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cyan-400 via-teal-500 to-emerald-500 shadow-[0_0_40px_rgba(6,182,212,0.6),0_0_80px_rgba(6,182,212,0.3)]" />
              
              {/* Glass overlay */}
              <div className="absolute inset-[2px] rounded-full bg-gradient-to-br from-white/30 via-transparent to-black/20" />
              
              {/* Inner glow ring */}
              <div className="absolute inset-[3px] rounded-full border border-white/40" />
              
              {/* Shine effect */}
              <motion.div
                animate={{ 
                  x: isHovered ? ['-100%', '200%'] : '-100%',
                  opacity: isHovered ? [0, 1, 0] : 0
                }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
                className="absolute inset-0 rounded-full overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-12" />
              </motion.div>

              {/* Icon container */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ 
                    scale: isHovered ? 1.1 : 1,
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <HiChatBubbleLeftRight className="text-white text-3xl drop-shadow-lg" />
                </motion.div>
              </div>

              {/* Sparkle effects */}
              <motion.div
                animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                className="absolute -top-1 -right-1"
              >
                <IoSparkles className="text-yellow-300 text-sm" />
              </motion.div>
              <motion.div
                animate={{ opacity: [0, 1, 0], scale: [0.5, 1, 0.5] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.7 }}
                className="absolute -bottom-1 -left-1"
              >
                <IoSparkles className="text-cyan-300 text-xs" />
              </motion.div>

              {/* Online indicator badge */}
              <div className="absolute -top-1 -right-1 w-6 h-6">
                <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-50" />
                <div className="relative w-full h-full bg-gradient-to-br from-green-400 to-emerald-600 rounded-full border-2 border-black flex items-center justify-center shadow-lg shadow-green-500/50">
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
              </div>
            </motion.button>

            {/* Floating label */}
            <AnimatePresence>
              {isHovered && (
                <motion.div
                  initial={{ opacity: 0, x: 10, scale: 0.9 }}
                  animate={{ opacity: 1, x: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 10, scale: 0.9 }}
                  className="absolute right-full mr-4 top-1/2 -translate-y-1/2 whitespace-nowrap"
                >
                  <div className="relative px-4 py-2.5 bg-gradient-to-r from-gray-900 to-black rounded-xl border border-cyan-500/40 shadow-xl shadow-cyan-500/20">
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500/10 to-transparent" />
                    <div className="relative flex items-center gap-2">
                      <FaRobot className="text-cyan-400" />
                      <span className="text-white font-medium text-sm">Chat with Zero-AI</span>
                    </div>
                    {/* Arrow */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1.5 w-3 h-3 bg-black border-r border-t border-cyan-500/40 rotate-45" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* PREMIUM CHAT WINDOW */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-8 right-8 z-[9999] w-[420px] h-[620px]"
          >
            {/* Outer glow */}
            <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-teal-400 to-emerald-500 rounded-3xl opacity-40 blur-xl" />
            <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-3xl opacity-60 blur-sm" />
            
            {/* Main container */}
            <div className="relative h-full bg-gradient-to-b from-gray-900 via-gray-950 to-black rounded-2xl border border-cyan-500/30 overflow-hidden flex flex-col">
              
              {/* Animated header gradient */}
              <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/20 via-teal-500/20 to-emerald-600/20" />
                <motion.div
                  animate={{ x: ['-100%', '100%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
                />
                
                <div className="relative p-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* AI Avatar */}
                    <div className="relative">
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full blur-md opacity-60"
                      />
                      <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-cyan-400 via-teal-500 to-emerald-500 flex items-center justify-center border-2 border-white/30 shadow-xl">
                        <FaRobot className="text-white text-2xl" />
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full border-2 border-gray-900 flex items-center justify-center">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-white font-bold text-lg">Zero-AI Assistant</h3>
                      <div className="flex items-center gap-2 text-sm text-cyan-400">
                        <span className="flex items-center gap-1.5">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          Online
                        </span>
                        <span className="text-gray-600">•</span>
                        <span className="text-gray-400">Instant Responses</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Close button */}
                  <motion.button
                    onClick={handleToggle}
                    className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:bg-red-500/20 hover:border-red-500/50 transition-all duration-300"
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IoClose size={22} />
                  </motion.button>
                </div>
                
                {/* Animated border */}
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ delay: index * 0.1, type: "spring", damping: 20 }}
                    className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      message.type === "user"
                        ? "bg-gradient-to-r from-cyan-500 to-teal-500 text-black rounded-br-md shadow-lg shadow-cyan-500/30"
                        : "bg-white/5 backdrop-blur-sm text-white rounded-bl-md border border-white/10"
                    }`}>
                      <p className="text-sm leading-relaxed">{message.text}</p>
                      <p className={`text-[10px] mt-1.5 ${message.type === "user" ? "text-black/50" : "text-gray-500"}`}>
                        {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </motion.div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="bg-white/5 rounded-2xl rounded-bl-md px-5 py-4 border border-white/10">
                      <div className="flex items-center gap-2">
                        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0 }} className="w-2.5 h-2.5 bg-cyan-400 rounded-full" />
                        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }} className="w-2.5 h-2.5 bg-cyan-400 rounded-full" />
                        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }} className="w-2.5 h-2.5 bg-cyan-400 rounded-full" />
                      </div>
                    </div>
                  </motion.div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input area */}
              <div className="p-5 border-t border-cyan-500/20 bg-black/50">
                <div className="flex items-center gap-3">
                  <div className="flex-1 relative group">
                    <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl opacity-0 group-focus-within:opacity-50 blur-sm transition-opacity duration-300" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask me anything..."
                      className="relative w-full px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-cyan-500/50 transition-all duration-300"
                    />
                  </div>
                  
                  <motion.button
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim()}
                    whileHover={inputValue.trim() ? { scale: 1.05 } : {}}
                    whileTap={inputValue.trim() ? { scale: 0.95 } : {}}
                    className={`relative w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-300 ${
                      inputValue.trim() ? "cursor-pointer" : "cursor-not-allowed"
                    }`}
                  >
                    {inputValue.trim() && (
                      <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl blur-md opacity-50" />
                    )}
                    <div className={`relative w-full h-full rounded-xl flex items-center justify-center ${
                      inputValue.trim()
                        ? "bg-gradient-to-r from-cyan-500 to-teal-500 shadow-lg shadow-cyan-500/30"
                        : "bg-white/5 border border-white/10"
                    }`}>
                      <IoSend size={20} className={inputValue.trim() ? "text-black" : "text-gray-500"} />
                    </div>
                  </motion.button>
                </div>
                
                <p className="text-[11px] text-gray-600 text-center mt-3 flex items-center justify-center gap-1">
                  <IoSparkles className="text-cyan-500" />
                  Powered by Zero-AI • Available 24/7
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AIChatWidget;
